<template>
  <Card class="tw-h-[210px]">
    <CardContent class="tw-pt-4">
      <div class="tw-flex tw-justify-between tw-mb-2">
        <CardTitle class="tw-uppercase tw-text-[0.8rem]">
          {{ t('profile.titles.main_info') }}
        </CardTitle>

        <div>
          <label for="uploadProfilePicture">
            <Button variant="secondary" size="sm" as="span" class="tw-cursor-pointer">
              <i class="icon-upload"></i>
              {{ t('profile.titles.picture') }}
            </Button>
          </label>

          <input
            type="file"
            id="uploadProfilePicture"
            class="tw-hidden"
            accept="image/png, image/jpeg"
            @change="onProfilePictureUploaded"
          />
        </div>
      </div>

      <div class="tw-flex tw-items-start tw-gap-4 tw-text-[#6d7287]">
        <img
          class="tw-w-20 tw-h-20 tw-rounded-full tw-object-cover"
          :src="currentUser?.profile_picture ? currentUser?.profile_picture : defaultProfilePicture"
          :alt="t('profile.titles.user_image')"
        />

        <div class="tw-flex tw-flex-col">
          <span v-if="!editUserName" class="tw-text-[0.95rem] tw-select-none">
            {{ currentUser?.first_name }} {{ currentUser?.last_name }}
            <i class="icon-edit tw-cursor-pointer" @click="changeEditMode"></i>
          </span>

          <div v-else class="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-mt-2">
            <div class="tw-flex tw-gap-2">
              <Input
                type="text"
                :default-value="currentUser?.first_name"
                ref="firstNameInputValue"
              />

              <Input type="text" :default-value="currentUser?.last_name" ref="lastNameInputValue" />
            </div>

            <Button
              variant="secondary"
              size="xs"
              class="tw-text-green-600"
              @click="confirmNameEditing"
            >
              ✓
            </Button>
            <Button variant="secondary" size="xs" class="tw-text-red-600" @click="changeEditMode">
              ✖
            </Button>
          </div>

          <span class="tw-text-[0.7rem] tw-mb-4" :title="currentUser?.user_role.join(', ')">
            {{ currentUser?.user_role.join(', ') }}
          </span>

          <div class="tw-flex tw-gap-4">
            <div class="tw-flex tw-flex-col tw-items-center">
              <span class="tw-text-cta-primary tw-font-bold tw-text-[0.8rem]">
                {{ currentUser?.projects_count }}
              </span>
              <span class="tw-text-[0.7rem]">{{ t('profile.titles.assigned_projects') }}</span>
            </div>

            <div class="tw-flex tw-flex-col tw-items-center">
              <span class="tw-text-cta-primary tw-font-bold tw-text-[0.8rem]">
                {{ currentUser?.customers_count }}
              </span>
              <span class="tw-text-[0.7rem]">{{ t('profile.titles.total_customers') }}</span>
            </div>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import defaultProfilePicture from '@/assets/images/user-avatar.jpg';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { useUserStore } from '@/stores/user.store';
import { storeToRefs } from 'pinia';
import { useFileValidation } from '@/_helpers/composables/file-validation.composable';
import { ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const { currentUser } = storeToRefs(useUserStore());
const { editUserProfile } = useUserStore();

const firstNameInputValue = ref();
const lastNameInputValue = ref();
const editUserName = ref(false);
const isPictureValid: Ref<boolean | null> = ref(null);

const onProfilePictureUploaded = async (e: any) => {
  const pictureFile: File = e.target.files[0];
  const { validateSize, validateTypeAndExtension } = useFileValidation();
  isPictureValid.value = validateSize(pictureFile, 0, 5) && validateTypeAndExtension(pictureFile);

  if (isPictureValid.value) {
    await editUserProfile({ profilePicture: pictureFile });
  }
};

const changeEditMode = () => {
  editUserName.value = !editUserName.value;
};

const confirmNameEditing = async () => {
  const enteredFirstName = firstNameInputValue.value.value;
  const enteredLastName = lastNameInputValue.value.value;

  await editUserProfile({
    userFirstName: enteredFirstName,
    userLastName: enteredLastName,
  });

  changeEditMode();
};
</script>

<style scoped>
/* Using Tailwind classes - no additional styles needed */
</style>
